import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getBirthdayMembers = createAsyncThunk(
    'birthday/getMembers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching birthday members with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_MEMBERS, { params });
            console.log('Birthday members response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Members retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get birthday members error:', error);

            let errorMessage = 'Failed to fetch members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get upcoming birthdays
 * ⚠️ API nests the array under `data.data` — normalised here so
 * the reducer can safely store a flat array.
 */
export const getUpcomingBirthdays = createAsyncThunk(
    'birthday/getUpcoming',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching upcoming birthdays with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_UPCOMING, { params });
            console.log('Upcoming birthdays response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                // ⬇️ NORMALISE — API nests the array under data.data
                const items = Array.isArray(data)
                    ? data
                    : (data?.data || []);

                return {
                    success: true,
                    message: message || 'Upcoming birthdays retrieved successfully',
                    data: items,                        // ← always an array
                    count: data?.count ?? items.length,
                    days: data?.days
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch upcoming birthdays',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get upcoming birthdays error:', error);

            let errorMessage = 'Failed to fetch upcoming birthdays';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch upcoming birthdays';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get birthday statistics
 */
export const getBirthdayStats = createAsyncThunk(
    'birthday/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching birthday statistics...');

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_STATS, { params });
            console.log('Birthday stats response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthday statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch birthday statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get birthday stats error:', error);

            let errorMessage = 'Failed to fetch birthday statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch birthday statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get birthday settings
 */
export const getBirthdaySettings = createAsyncThunk(
    'birthday/getSettings',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching birthday settings...');

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_SETTINGS, { params });
            console.log('Birthday settings response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthday settings retrieved successfully',
                    settings: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch birthday settings',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get birthday settings error:', error);

            let errorMessage = 'Failed to fetch birthday settings';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch birthday settings';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Update birthday settings
 */
export const updateBirthdaySettings = createAsyncThunk(
    'birthday/updateSettings',
    async (settingsData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating birthday settings...');
            console.log('Settings data:', settingsData);

            const response = await apiClient.put(API_ENDPOINTS.BIRTHDAY_SETTINGS, settingsData);
            console.log('Update settings response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthday settings updated successfully',
                    settings: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update birthday settings',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update birthday settings error:', error);

            let errorMessage = 'Failed to update birthday settings';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update birthday settings';

                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Send birthday message to a member
 */
export const sendBirthdayMessage = createAsyncThunk(
    'birthday/sendMessage',
    async ({ memberId, data = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Sending birthday message to member:', memberId);
            console.log('Message data:', data);

            const response = await apiClient.post(API_ENDPOINTS.BIRTHDAY_SEND(memberId), data);
            console.log('Send message response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthday message sent successfully',
                    result: responseData || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send birthday message',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Send birthday message error:', error);

            let errorMessage = 'Failed to send birthday message';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send birthday message';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Send bulk birthday messages
 */
export const sendBulkBirthdayMessages = createAsyncThunk(
    'birthday/sendBulk',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Sending bulk birthday messages...');
            console.log('Bulk data:', data);

            const response = await apiClient.post(API_ENDPOINTS.BIRTHDAY_SEND_BULK, data);
            console.log('Send bulk response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Bulk messages sent successfully',
                    result: responseData || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send bulk messages',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Send bulk birthday messages error:', error);

            let errorMessage = 'Failed to send bulk messages';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send bulk messages';

                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Export birthday data
 */
export const exportBirthdayData = createAsyncThunk(
    'birthday/export',
    async ({ type, format = 'csv' }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Exporting birthday data:', type, 'format:', format);

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_EXPORT(type), {
                params: { format }
            });
            console.log('Export response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type,
                    format: format
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Export birthday data error:', error);

            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get member birthday details
 */
export const getMemberBirthday = createAsyncThunk(
    'birthday/getMember',
    async (memberId, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching member birthday details:', memberId);

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_MEMBER(memberId));
            console.log('Member birthday response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member birthday details retrieved successfully',
                    member: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch member birthday details',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get member birthday error:', error);

            let errorMessage = 'Failed to fetch member birthday details';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch member birthday details';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Update member birthday
 */
export const updateMemberBirthday = createAsyncThunk(
    'birthday/updateMember',
    async ({ memberId, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Updating member birthday:', memberId);
            console.log('Update data:', data);

            const response = await apiClient.put(API_ENDPOINTS.BIRTHDAY_MEMBER(memberId), data);
            console.log('Update member response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member birthday updated successfully',
                    member: responseData || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update member birthday',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Update member birthday error:', error);

            let errorMessage = 'Failed to update member birthday';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update member birthday';

                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Test birthday automation
 */
export const testBirthdayAutomation = createAsyncThunk(
    'birthday/testAutomation',
    async (data = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Testing birthday automation...');

            const response = await apiClient.post(API_ENDPOINTS.BIRTHDAY_TEST, data);
            console.log('Test automation response:', response.data);

            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthday automation test completed',
                    result: responseData || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to test birthday automation',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Test birthday automation error:', error);

            let errorMessage = 'Failed to test birthday automation';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to test birthday automation';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

/**
 * Get birthday report
 */
export const getBirthdayReport = createAsyncThunk(
    'birthday/getReport',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            console.log('Fetching birthday report with params:', params);

            const response = await apiClient.get(API_ENDPOINTS.BIRTHDAY_REPORT, { params });
            console.log('Birthday report response:', response.data);

            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Birthday report generated successfully',
                    report: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to generate birthday report',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            console.error('Get birthday report error:', error);

            let errorMessage = 'Failed to generate birthday report';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to generate birthday report';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    members: [],
    upcoming: [],
    stats: {
        total_members: 0,
        with_birthday: 0,
        without_birthday: 0,
        messages_sent_this_year: 0,
        upcoming_in_7_days: 0,
        stats: {
            total: 0,
            active: 0,
            inactive: 0,
            total_sent: 0,
            avg_response_rate: 0
        },
        birthdays_by_month: {}
    },
    settings: {
        send_time: '08:00',
        // sender_id: 'CHURCH',
        message_template: '',
        notify_pastor: true,
        send_anniversary: true,
        timezone: 'UTC',
        is_active: true,
        pastor_name: ''
    },
    currentMember: null,
    exportData: null,
    report: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
    testResult: null
};

// ============================================
// SLICE
// ============================================

const birthdaySlice = createSlice({
    name: 'birthday',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearBirthdayState: (state) => {
            state.members = [];
            state.upcoming = [];
            state.stats = initialState.stats;
            state.settings = initialState.settings;
            state.currentMember = null;
            state.exportData = null;
            state.report = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
            state.testResult = null;
        },
        clearCurrentMember: (state) => {
            state.currentMember = null;
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
        clearReport: (state) => {
            state.report = null;
        },
        clearTestResult: (state) => {
            state.testResult = null;
        },
        setSettings: (state, action) => {
            state.settings = { ...state.settings, ...action.payload };
        },
        resetSettings: (state) => {
            state.settings = initialState.settings;
        },
        updateMemberInList: (state, action) => {
            const updatedMember = action.payload;
            const index = state.members.findIndex(m => m.id === updatedMember.id);
            if (index !== -1) {
                state.members[index] = { ...state.members[index], ...updatedMember };
            }
        },
        addUpcomingBirthday: (state, action) => {
            const newBirthday = action.payload;
            // Check if already exists
            const exists = state.upcoming.some(b => b.id === newBirthday.id);
            if (!exists) {
                state.upcoming.push(newBirthday);
                // Sort by days until
                state.upcoming.sort((a, b) => a.days - b.days);
            }
        },
        removeUpcomingBirthday: (state, action) => {
            const id = action.payload;
            state.upcoming = state.upcoming.filter(b => b.id !== id);
        }
    },
    extraReducers: (builder) => {
        builder
            // ===== GET BIRTHDAY MEMBERS =====
            .addCase(getBirthdayMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBirthdayMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.members = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.stats = { ...state.stats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Members retrieved successfully';
                state.apiMessage = payload.message || 'Members retrieved successfully';
                state.error = null;
            })
            .addCase(getBirthdayMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch members';
                state.apiMessage = action.payload?.message || 'Failed to fetch members';
                state.successMessage = null;
                state.members = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET UPCOMING BIRTHDAYS =====
            .addCase(getUpcomingBirthdays.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getUpcomingBirthdays.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                // Thunk already normalised — payload.data is guaranteed to be an array.
                // Defensive guard in case anything upstream ever changes.
                state.upcoming = Array.isArray(payload.data) ? payload.data : [];
                state.successMessage = payload.message || 'Upcoming birthdays retrieved successfully';
                state.apiMessage = payload.message || 'Upcoming birthdays retrieved successfully';
                state.error = null;
            })
            .addCase(getUpcomingBirthdays.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch upcoming birthdays';
                state.apiMessage = action.payload?.message || 'Failed to fetch upcoming birthdays';
                state.successMessage = null;
                state.upcoming = [];
            })

            // ===== GET BIRTHDAY STATS =====
            .addCase(getBirthdayStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBirthdayStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = { ...state.stats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Birthday statistics retrieved successfully';
                state.apiMessage = payload.message || 'Birthday statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getBirthdayStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch birthday statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch birthday statistics';
                state.successMessage = null;
            })

            // ===== GET BIRTHDAY SETTINGS =====
            .addCase(getBirthdaySettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBirthdaySettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.settings = { ...state.settings, ...(payload.settings || {}) };
                state.successMessage = payload.message || 'Birthday settings retrieved successfully';
                state.apiMessage = payload.message || 'Birthday settings retrieved successfully';
                state.error = null;
            })
            .addCase(getBirthdaySettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch birthday settings';
                state.apiMessage = action.payload?.message || 'Failed to fetch birthday settings';
                state.successMessage = null;
            })

            // ===== UPDATE BIRTHDAY SETTINGS =====
            .addCase(updateBirthdaySettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateBirthdaySettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.settings = { ...state.settings, ...(payload.settings || {}) };
                state.successMessage = payload.message || 'Birthday settings updated successfully';
                state.apiMessage = payload.message || 'Birthday settings updated successfully';
                state.error = null;
            })
            .addCase(updateBirthdaySettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update birthday settings';
                state.apiMessage = action.payload?.message || 'Failed to update birthday settings';
                state.successMessage = null;
            })

            // ===== SEND BIRTHDAY MESSAGE =====
            .addCase(sendBirthdayMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendBirthdayMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Birthday message sent successfully';
                state.apiMessage = payload.message || 'Birthday message sent successfully';
                state.error = null;
                // Update stats
                if (state.stats) {
                    state.stats.messages_sent_this_year = (state.stats.messages_sent_this_year || 0) + 1;
                    state.stats.stats = state.stats.stats || {};
                    state.stats.stats.total_sent = (state.stats.stats.total_sent || 0) + 1;
                }
            })
            .addCase(sendBirthdayMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send birthday message';
                state.apiMessage = action.payload?.message || 'Failed to send birthday message';
                state.successMessage = null;
            })

            // ===== SEND BULK BIRTHDAY MESSAGES =====
            .addCase(sendBulkBirthdayMessages.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendBulkBirthdayMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const result = payload.result || {};
                state.successMessage = payload.message || 'Bulk messages sent successfully';
                state.apiMessage = payload.message || 'Bulk messages sent successfully';
                state.error = null;
                // Update stats
                if (result.sent && state.stats) {
                    state.stats.messages_sent_this_year = (state.stats.messages_sent_this_year || 0) + result.sent;
                    state.stats.stats = state.stats.stats || {};
                    state.stats.stats.total_sent = (state.stats.stats.total_sent || 0) + result.sent;
                }
            })
            .addCase(sendBulkBirthdayMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send bulk messages';
                state.apiMessage = action.payload?.message || 'Failed to send bulk messages';
                state.successMessage = null;
            })

            // ===== EXPORT BIRTHDAY DATA =====
            .addCase(exportBirthdayData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportBirthdayData.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportBirthdayData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            })

            // ===== GET MEMBER BIRTHDAY =====
            .addCase(getMemberBirthday.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMemberBirthday.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentMember = payload.member || null;
                state.successMessage = payload.message || 'Member birthday details retrieved successfully';
                state.apiMessage = payload.message || 'Member birthday details retrieved successfully';
                state.error = null;
            })
            .addCase(getMemberBirthday.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch member birthday details';
                state.apiMessage = action.payload?.message || 'Failed to fetch member birthday details';
                state.successMessage = null;
                state.currentMember = null;
            })

            // ===== UPDATE MEMBER BIRTHDAY =====
            .addCase(updateMemberBirthday.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateMemberBirthday.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentMember = payload.member || null;
                state.successMessage = payload.message || 'Member birthday updated successfully';
                state.apiMessage = payload.message || 'Member birthday updated successfully';
                state.error = null;
                // Update in members list
                if (payload.member) {
                    const index = state.members.findIndex(m => m.id === payload.member.id);
                    if (index !== -1) {
                        state.members[index] = { ...state.members[index], ...payload.member };
                    }
                }
            })
            .addCase(updateMemberBirthday.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update member birthday';
                state.apiMessage = action.payload?.message || 'Failed to update member birthday';
                state.successMessage = null;
            })

            // ===== TEST BIRTHDAY AUTOMATION =====
            .addCase(testBirthdayAutomation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(testBirthdayAutomation.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.testResult = payload.result || null;
                state.successMessage = payload.message || 'Birthday automation test completed';
                state.apiMessage = payload.message || 'Birthday automation test completed';
                state.error = null;
            })
            .addCase(testBirthdayAutomation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to test birthday automation';
                state.apiMessage = action.payload?.message || 'Failed to test birthday automation';
                state.successMessage = null;
                state.testResult = null;
            })

            // ===== GET BIRTHDAY REPORT =====
            .addCase(getBirthdayReport.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getBirthdayReport.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.report = payload.report || null;
                state.successMessage = payload.message || 'Birthday report generated successfully';
                state.apiMessage = payload.message || 'Birthday report generated successfully';
                state.error = null;
            })
            .addCase(getBirthdayReport.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to generate birthday report';
                state.apiMessage = action.payload?.message || 'Failed to generate birthday report';
                state.successMessage = null;
                state.report = null;
            });
    },
});

// ============================================
// ACTIONS
// ============================================

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearBirthdayState,
    clearCurrentMember,
    clearExportData,
    clearReport,
    clearTestResult,
    setSettings,
    resetSettings,
    updateMemberInList,
    addUpcomingBirthday,
    removeUpcomingBirthday
} = birthdaySlice.actions;

// ============================================
// SELECTORS
// ============================================

export const selectBirthdayMembers = (state) => state.birthday?.members || [];
export const selectBirthdayUpcoming = (state) => state.birthday?.upcoming || [];
export const selectBirthdayStats = (state) => state.birthday?.stats || initialState.stats;
export const selectBirthdaySettings = (state) => state.birthday?.settings || initialState.settings;
export const selectBirthdayPagination = (state) => state.birthday?.pagination || initialState.pagination;
export const selectBirthdayCurrentMember = (state) => state.birthday?.currentMember || null;
export const selectBirthdayLoading = (state) => state.birthday?.isLoading || false;
export const selectBirthdayError = (state) => state.birthday?.error || null;
export const selectBirthdaySuccess = (state) => state.birthday?.successMessage || null;
export const selectBirthdayApiMessage = (state) => state.birthday?.apiMessage || null;
export const selectBirthdayExportData = (state) => state.birthday?.exportData || null;
export const selectBirthdayReport = (state) => state.birthday?.report || null;
export const selectBirthdayTestResult = (state) => state.birthday?.testResult || null;

// ============================================
// COMPOSED SELECTORS
// ============================================

/**
 * Get formatted birthday statistics summary
 */
export const selectBirthdayStatsSummary = (state) => {
    const stats = selectBirthdayStats(state);
    if (!stats) return null;
    return {
        totalMembers: stats.total_members || 0,
        withBirthday: stats.with_birthday || 0,
        withoutBirthday: stats.without_birthday || 0,
        messagesSent: stats.messages_sent_this_year || 0,
        upcomingIn7Days: stats.upcoming_in_7_days || 0,
        avgResponseRate: stats.stats?.avg_response_rate || 0,
        totalSent: stats.stats?.total_sent || 0,
        active: stats.stats?.active || 0,
        inactive: stats.stats?.inactive || 0,
        birthdaysByMonth: stats.birthdays_by_month || {}
    };
};

/**
 * Get upcoming birthdays with formatted dates
 */
export const selectFormattedUpcomingBirthdays = (state) => {
    const upcoming = selectBirthdayUpcoming(state);
    return upcoming.map(b => ({
        ...b,
        formattedDate: b.dob || 'N/A',
        daysLabel: b.days === 0 ? '🎉 Today!' : b.days === 1 ? 'Tomorrow' : `${b.days} days`,
        isToday: b.days === 0,
        isTomorrow: b.days === 1,
        status: b.days === 0 ? 'today' : b.days <= 7 ? 'soon' : 'upcoming'
    }));
};

/**
 * Get settings with defaults
 */
export const selectBirthdaySettingsWithDefaults = (state) => {
    const settings = selectBirthdaySettings(state);
    return {
        send_time: settings.send_time || '08:00',
        sender_id: settings.sender_id || 'CHURCH',
        message_template: settings.message_template || '',
        notify_pastor: settings.notify_pastor !== undefined ? settings.notify_pastor : true,
        send_anniversary: settings.send_anniversary !== undefined ? settings.send_anniversary : true,
        timezone: settings.timezone || 'UTC',
        is_active: settings.is_active !== undefined ? settings.is_active : true,
        pastor_name: settings.pastor_name || 'Pastor'
    };
};

/**
 * Get birthday report summary
 */
export const selectBirthdayReportSummary = (state) => {
    const report = selectBirthdayReport(state);
    if (!report) return null;
    return {
        year: report.year,
        month: report.month,
        type: report.type,
        generatedAt: report.generated_at,
        monthlyData: report.monthly_data || {},
        yearlyTotal: report.yearly_total || 0,
        topMonths: report.top_months || []
    };
};

/**
 * Get members with birthday data status
 */
export const selectMembersWithBirthdayStatus = (state) => {
    const members = selectBirthdayMembers(state);
    return members.map(m => ({
        ...m,
        hasBirthdayData: !!(m.birthday || m.dob),
        birthdayDate: m.birthday?.dob || m.dob || null,
        sendBirthday: m.birthday?.send_birthday !== undefined ? m.birthday.send_birthday : true,
        sendAnniversary: m.birthday?.send_anniversary !== undefined ? m.birthday.send_anniversary : true
    }));
};

export default birthdaySlice.reducer;